import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore, useThemeStore } from './stores';
import { ToastProvider } from './components/ui/toast';
import ScrollToTop from './components/ScrollToTop';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Meetings from './pages/Meetings';
import MeetingRecordings from './pages/MeetingRecordings';
import MeetingParticipants from './pages/MeetingParticipants';
import LiveStreams from './pages/LiveStreams';
import LivestreamRecordings from './pages/LivestreamRecordings';
import PublicStreamPublisher from './pages/PublicStreamPublisher';
import PublicStreamViewer from './pages/PublicStreamViewer';
import PublicMeetingRoom from './components/PublicMeetingRoom';
import Profile from './pages/Profile';

import Apps from './pages/Apps';
import AppApiDocs from './pages/AppApiDocs';
import Users from './pages/Users';
import Roles from './pages/Roles';
import { Skeleton } from './components/ui/skeleton';

// App Initializer component
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const initializeTheme = useThemeStore((state) => state.initializeTheme);

  useEffect(() => {
    initializeAuth();
    initializeTheme();
  }, [initializeAuth, initializeTheme]);

  return <>{children}</>;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}</> : <Navigate to="/login" />;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center auth-bg">
        <div className="space-y-4 text-center">
          <Skeleton className="h-8 w-8 rounded-full mx-auto" />
          <Skeleton className="h-4 w-24 mx-auto" />
        </div>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard/meetings" /> : <>{children}</>;
};

function App() {
  return (
    <ToastProvider>
      <AppInitializer>
        <Router>
          <ScrollToTop />
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              <Route
                path="/reset-password/:token"
                element={
                  <PublicRoute>
                    <ResetPassword />
                  </PublicRoute>
                }
              />

              {/* Protected routes */}
              <Route
                path="/dashboard/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/meetings"
                element={
                  <ProtectedRoute>
                    <Meetings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/meetings/:meetingId/recordings"
                element={
                  <ProtectedRoute>
                    <MeetingRecordings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/meetings/:id/participants"
                element={
                  <ProtectedRoute>
                    <MeetingParticipants />
                  </ProtectedRoute>
                }
              />
              
              {/* Apps route */}
              <Route
                path="/dashboard/apps"
                element={
                  <ProtectedRoute>
                    <Apps />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/apps/:appId/api-docs"
                element={
                  <ProtectedRoute>
                    <AppApiDocs />
                  </ProtectedRoute>
                }
              />

              {/* Users route */}
              <Route
                path="/dashboard/users"
                element={
                  <ProtectedRoute>
                    <Users />
                  </ProtectedRoute>
                }
              />

              {/* Roles route */}
              <Route
                path="/dashboard/roles"
                element={
                  <ProtectedRoute>
                    <Roles />
                  </ProtectedRoute>
                }
              />
              
              {/* Live Stream routes */}
              <Route
                path="/dashboard/livestreams"
                element={
                  <ProtectedRoute>
                    <LiveStreams />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard/livestream/:livestreamId/recordings"
                element={
                  <ProtectedRoute>
                    <LivestreamRecordings />
                  </ProtectedRoute>
                }
              />

              {/* Public routes (no authentication required) */}
              <Route path="/public/stream/:streamId/publish" element={<PublicStreamPublisher />} />
              <Route path="/public/stream/:streamId/view" element={<PublicStreamViewer />} />
              <Route path="/public/meeting/:meetingId/:token" element={<PublicMeetingRoom />} />

              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard/meetings" />} />
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/dashboard/meetings" />} />
            </Routes>
          </div>
        </Router>
      </AppInitializer>
    </ToastProvider>
  );
}

export default App;
